import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useLogin } from '../../context/LoginContext';
import './LoginForm.css';
import logger from '../../utils/logger';

const usernameSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#9b9b9b" height="24" width="24" size="24" color="rgb(224,224,224)">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"></path>
  </svg>
);

const passwordSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#9b9b9b" width="22px" height="22px" size="24" color="rgb(224,224,224)">
    <path d="M0 0h24v24H0z" fill="none"></path>
    <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z"></path>
  </svg>
);

const LoginForm = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { login } = useLogin();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setErrors({});

    try {
      const { success, creds } = await login(formData);
      if (success) {
        // Save Matrix credentials to localStorage for session persistence
        localStorage.setItem('matrixCreds', JSON.stringify(creds));
        navigate('/conversations');
      } else {
        setErrors({ general: 'Login failed. Please try again.' });
      }
    } catch (error) {
      logger.error('Login failed:', error);
      setErrors({ general: 'Login failed. Please try again.' });
    }
    setIsLoading(false);
  };

  return (
    <div className="login-form-wrapper">
      <img className="login-form-logo" src="images/netsfereLogoWithText.png" alt="Netsfere Logo" />
      <div className="login-form-header">{t('LoginPage.loginToYourAccount')}</div>
      <form className="login-form" onSubmit={handleLogin}>
          <div className="login-form-group">
            <label htmlFor="username" className="login-label">
              Username
            </label>
            <div className="login-form-input-container">
              {usernameSVG}
              <input
                id="username"
                name="username"
                className={`login-input ${errors.username ? 'error' : ''}`}
                placeholder="Enter your Matrix username (e.g., @user:server.com)"
                value={formData.username}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>
            {errors.username && <span className="login-error">{errors.username}</span>}
          </div>
          <div className="login-form-group">
            <label htmlFor="password" className="login-label">
              {t('LoginPage.passwordInputLabel')}
            </label>
            <div className="login-form-input-container">
              {passwordSVG}
              <input
                id="password"
                name="password"
                type="password"
                className={`login-input ${errors.password ? 'error' : ''}`}
                placeholder={t('LoginPage.passwordInputHint')}
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
              />
            </div>
            {errors.password && <span className="login-error">{errors.password}</span>}
          </div>
          
          <a className="login-form-reset-password">
            {t('LoginPage.forgotPassword')}
          </a>
          
          {errors.general && <div className="login-error" style={{ marginBottom: '1rem' }}>{errors.general}</div>}
          
          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? 'Signing in...' : t('LoginPage.login')}
          </button>
        </form>
    </div>
  );
};

export default LoginForm;
